import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const InventoryTurnover = () => {
  const [costOfGoodsSold, setCostOfGoodsSold] = useState("");
  const [averageInventory, setAverageInventory] = useState("");
  const [inventoryTurnover, setInventoryTurnover] = useState("0.00");

  // Function to calculate Inventory Turnover
  const calculateInventoryTurnover = (costOfGoodsSold, averageInventory) => {
    if (
      costOfGoodsSold &&
      averageInventory &&
      parseFloat(averageInventory) !== 0
    ) {
      const turnover =
        parseFloat(costOfGoodsSold) / parseFloat(averageInventory);
      setInventoryTurnover(turnover.toFixed(2));
    } else {
      setInventoryTurnover("0.00");
    }
  };

  // useEffect to recalculate when input changes
  useEffect(() => {
    calculateInventoryTurnover(costOfGoodsSold, averageInventory);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [costOfGoodsSold, averageInventory]);

  // Handlers for input changes
  const handleCostOfGoodsSoldChange = (e) => {
    setCostOfGoodsSold(e.target.value);
  };

  const handleAverageInventoryChange = (e) => {
    setAverageInventory(e.target.value);
  };

  return (
    <Layout title="Inventory Turnover Calculator" path="inventory-turnover">
      <section
        className="inventory-turnover-section section-padding"
        id="inventory-turnover"
      >
        <div className="container mt-5">
          <div className="row">
            {/* Inventory Turnover Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Inventory Turnover Calculator
                </h3>
                <div className="container">
                  {/* Input Fields for Inventory Turnover Calculation */}
                  <div className="row gy-3">
                    {/* Cost of Goods Sold */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="costOfGoodsSold" className="form-label">
                        Cost of Goods Sold (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Cost of Goods Sold:</strong> The total cost of goods sold during the specified period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="costOfGoodsSold"
                        placeholder="Enter cost of goods sold"
                        value={costOfGoodsSold}
                        onChange={handleCostOfGoodsSoldChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the total cost of goods sold for the period
                      </small> */}
                    </div>

                    {/* Average Inventory */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="averageInventory" className="form-label">
                        Average Inventory (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Inventory:</strong> The average value of inventory held during the specified period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="averageInventory"
                        placeholder="Enter average inventory value"
                        value={averageInventory}
                        onChange={handleAverageInventoryChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the average inventory during the period
                      </small> */}
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Inventory Turnover:{" "}
                        <span className="fw-bold ms-3">
                          {inventoryTurnover}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Inventory Turnover Calculator
                </h3>

                {/* What is an Inventory Turnover Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Inventory Turnover Calculator?
                </h4>
                <p>
                  An **Inventory Turnover Calculator** helps measure the
                  efficiency of a business in managing its inventory over a
                  period.
                  {/* It is calculated using the following formula: */}
                </p>
                {/* <p className="fw-bold">Inventory Turnover = Cost of Goods Sold / Average Inventory</p> */}

                {/* Why Use an Inventory Turnover Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Inventory Turnover Calculator?
                </h4>
                <p>
                  There are several benefits of using an inventory turnover
                  calculator:
                </p>
                <ul>
                  <li>
                    <strong>Optimizes Inventory Levels:</strong> Helps
                    businesses maintain the right inventory levels to meet
                    demand.
                  </li>
                  <li>
                    <strong>Identifies Overstock/Understock Situations:</strong>{" "}
                    Assists in preventing excessive or insufficient inventory.
                  </li>
                  <li>
                    <strong>Improves Cash Flow:</strong> Aligns stock with
                    customer demand, enhancing cash flow management.
                  </li>
                </ul>

                {/* Suitable for */}
                <h4 className="mt-4 mb-3">
                  Who is this Calculator Suitable For?
                </h4>
                <p>This calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Retail Businesses</strong>
                  </li>
                  <li>
                    <strong>Manufacturing Companies</strong>
                  </li>
                  <li>
                    <strong>E-commerce Stores</strong>
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How often should I calculate my inventory turnover?
                </h4>
                <p>
                  It is recommended to calculate inventory turnover regularly,
                  such as quarterly or annually, to ensure effective inventory
                  management and make necessary adjustments.
                </p>

                <h4 className="mt-4 mb-3">
                  What is a good inventory turnover ratio?
                </h4>
                <p>
                  The ideal inventory turnover ratio can vary by industry, but a
                  higher ratio often indicates efficient management. However,
                  extremely high turnover may signal understocking, leading to
                  potential missed sales opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InventoryTurnover;
